
import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import posed from 'react-pose';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { RootState } from 'reducers/rootReducer';
import { messages } from 'config/messages';
import { selectors as sketchPersistenceSelectors, actions as sketchPersistenceActions } from 'ducks/persistence/sketchPersistence';
import { selectors as saveSketchModalSelectors, actions as saveSketchModalActions } from 'ducks/modal/saveSketchModal';
import { selectors as orderDetailSelectors } from 'ducks/orderDetails/orderDetails';
import { selectors as unsavedModalSelectors } from 'ducks/modal/unsavedModal';
import { ReactComponent as IconClose } from 'assets/icons/x.svg';
import { positionedModal, fade } from 'components/animations';
import { ModalWallResize, ButtonBig, ModalBackdrop } from 'components/modal/Modal';
import { sidebarItemButtonStyles } from 'components/sidebar/styles';
import { isWorkbenchApplication, doSketchExit } from 'effects/auth';

const Backdrop = styled(ModalBackdrop)({
  display: 'flex',
  flexFlow: 'column nowrap',
  alignItems: 'center',
  justifyContent: 'center',
});

const saveSketchInputStyles = css`
  width: 100%;
  pointer-events: none;
  margin-bottom: 12px;
`;

const saveSketchFormStyles = css`
  width: 100%;
  display: flex;
  flexDirection: column;

  > div {
    margin-right: auto;
    padding-top: 12px;
  }

  > label {
    width: 100%;
    > input {
      width: 100%;
    }
  }
`;

const BackdropAnimated = posed(Backdrop)(fade);
const ModalAnimated = posed(ModalWallResize)(positionedModal);
const CloseButton = styled.button(sidebarItemButtonStyles);

interface StateProps {
  readonly currentSketch: any;
  readonly isCreated: boolean;
  readonly isShowingSaveSketchModal: boolean;
  readonly orderId: string;
  readonly orderAddress: string;
  readonly logoutAfterSave: boolean;
}
interface ActionProps {
  readonly close: () => void;
  readonly saveSketchToApi: (payload?: any) => void;
}

type Props = StateProps & ActionProps;

interface State {
  sketchName: string;
  address: string;
  orderId: string;
}

class SaveSketchModal extends Component<Props, State> {
  // eslint-disable-next-line react/sort-comp
  private readonly focusRef: React.RefObject<HTMLInputElement>;

  public constructor(props: Props) {
    super(props);
    this.focusRef = createRef();

    const { currentSketch, orderAddress, orderId } = props;

    // Default from Redux
    const sketchName = currentSketch?.sketch_name ?? '';
    let address;
    let id;

    if (isWorkbenchApplication()) {
      // If we are coming from workbench, use the workbench values.
      address = orderAddress;
      id = orderId;
    } else {
      // Otherwise, default to what's on the sketch, or an empty value.
      address = currentSketch?.property_address ?? '';
      id = currentSketch?.order_id ?? '';
    }

    this.state = {
      sketchName,
      address,
      orderId: id,
    };
  }

  public componentDidMount(): void {
    this.focusRef.current!.focus();
  }

  private handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      this.save();
    }
    if (event.key === 'Escape') {
      this.close();
    }
  };

  public handleUserInput(event: React.ChangeEvent<HTMLInputElement>, type: string): void {
    const { value } = event.target;

    this.setState((prevState) => {
      const updates: State = {
        sketchName: prevState.sketchName,
        address: prevState.address,
        orderId: prevState.orderId,
      };
      if (type === 'sketchName' && prevState.sketchName !== value) {
        updates.sketchName = value;
      } else if (type === 'address' && prevState.address !== value) {
        updates.address = value;
      } else if (type === 'orderId' && prevState.orderId !== value) {
        updates.orderId = value;
      }
      return updates;
    });
  }

  private save = (event?: React.KeyboardEvent<HTMLInputElement> | React.FormEvent<HTMLFormElement>): void => {
    if (event) {
      event.preventDefault();
    }

    const { sketchName, address, orderId } = this.state;
    const { close, saveSketchToApi } = this.props;

    saveSketchToApi({ sketchName, propertyAddress: address, orderId, followUp: this.saveFollowUp });
    close();
  };

  private saveFollowUp= (): void => {
    const { logoutAfterSave } = this.props;
    if (logoutAfterSave) {
      const { orderId } = this.state;
      doSketchExit(orderId);
    }
  }

  private close = (): void => {
    const { close } = this.props;
    close();
  };

  public render = (): JSX.Element | null => {
    const { isCreated } = this.props;
    const { sketchName, address, orderId } = this.state;

    const cancelEvent = (event: React.PointerEvent | React.WheelEvent | React.DragEvent): void => {
      event.stopPropagation();
    };

    // For workbench, we are making the OrderId and Address Fields readOnly.
    const isWorkbenchMode = isWorkbenchApplication();

    return (
      <BackdropAnimated
        onPointerDown={cancelEvent}
        onPointerMove={cancelEvent}
        onPointerUp={cancelEvent}
        onPointerLeave={cancelEvent}
        onWheel={cancelEvent}
        onDragOver={cancelEvent}
        onDrop={cancelEvent}
        pose="enter"
        initialPose="exit"
      >
        <ModalAnimated
          pose="enter"
          initialPose="exit"
          position={window.screen.width > 400 ? { top: '15rem', left: '90%' } : { top: '15rem', left: '1rem' }}
          style={window.screen.width > 400 ? { width: '30%' } : { width: '85%' }}
        >
          <h2>
            <CloseButton onClick={this.close} title={messages.closeModal} style={{ cursor: 'pointer' }}>
              <IconClose />
            </CloseButton>
            <span>{isCreated ? 'Save Sketch' : 'Create Sketch'}</span>
          </h2>
          <form onSubmit={this.save} css={saveSketchFormStyles}>
            <div>Sketch Name</div>
            <label htmlFor="Sketch Name">
              <input
                css={saveSketchInputStyles}
                id="Sketch Name"
                ref={this.focusRef}
                type="text"
                value={sketchName}
                placeholder="Sketch Name"
                onChange={(e: any) => this.handleUserInput(e, 'sketchName')}
                onKeyDown={this.handleKeyDown}
              />
            </label>
            <div>Property Address</div>
            <label htmlFor="Property Address">
              <input
                css={saveSketchInputStyles}
                id="Property Address"
                type="text"
                value={address}
                placeholder="Property Address"
                readOnly={isWorkbenchMode}
                onChange={(e: any) => this.handleUserInput(e, 'address')}
                onKeyDown={this.handleKeyDown}
              />
            </label>

            <div>Order Id</div>
            <label htmlFor="Order Id">
              <input
                css={saveSketchInputStyles}
                id="Order Id"
                type="text"
                value={orderId}
                placeholder="Order Id"
                readOnly={isWorkbenchMode}
                onChange={(e: any) => this.handleUserInput(e, 'orderId')}
                onKeyDown={this.handleKeyDown}
              />
            </label>
            <div style={{ marginLeft: 'auto', marginRight: '11px' }}>
              <ButtonBig type="submit">Save Sketch</ButtonBig>
            </div>
          </form>
        </ModalAnimated>
      </BackdropAnimated>
    );
  };
}

export default connect(
  (state: RootState): StateProps => ({
    isShowingSaveSketchModal: saveSketchModalSelectors.isShowing(state),
    currentSketch: sketchPersistenceSelectors.getSketch(state),
    isCreated: sketchPersistenceSelectors.isCreated(state),
    orderId: orderDetailSelectors.getOrderId(state),
    orderAddress: orderDetailSelectors.getAddress(state),
    logoutAfterSave: unsavedModalSelectors.isQueued(state),
  }),
  {
    close: saveSketchModalActions.hide,
    saveSketchToApi: sketchPersistenceActions.saveSketch,
  },
)(SaveSketchModal);
